// ════════════════════════════════════════════════════════════════════════
//  Die zwei Aufrufe, die die Lern-App macht
//
//  Kein Supabase-SDK: Schüler haben keinen JWT, ein Auth-SDK hätte hier keine
//  Aufgabe außer Gewicht und einem Storage-Residuum auf dem Koffer-iPad.
//
//  Die Fehlerklassifizierung ist der Kern dieser Datei. Für die Retry-Queue muss
//  klar sein: „Code falsch“ (4xx) wird verworfen, „Netz weg“ (5xx/offline) wird
//  wiederholt. Ein 4xx endlos zu wiederholen ist der klassische Fehler.
//
//  ZWEI VERSCHIEDENE WEGE, mit Absicht:
//
//    Anmelden  → /functions/v1/code-login   (Edge Function)
//    Speichern → /rest/v1/rpc/…             (direkt)
//
//  Nur das Anmelden ist durchprobierbar, und nur davor sitzt deshalb die
//  Bremsschwelle. Das Speichern setzt einen gültigen Code voraus und muss auch
//  dann durchgehen, wenn eine ganze Klasse gleichzeitig fertig wird – hinter
//  einem Limit würde die Warteschlange sich selbst stauen.
//
//  Kein Schalter „direkt oder über die Function“: ein Codepfad. Eine
//  Umgebungsvariable, deren Vergessen das Rate-Limiting still abschaltet, ist
//  genau die Fehlerklasse, die dieses Projekt schon mehrfach getroffen hat.
//  Preis dafür: lokal muss `supabase functions serve --env-file supabase/.env`
//  laufen, siehe LOKAL.md.
// ════════════════════════════════════════════════════════════════════════

export type BackendConfig = {
  readonly url: string;
  readonly anonKey: string;
};

export type ErrorKind =
  /** Code falsch, Lizenz abgelaufen, unsinnige Werte – Wiederholen hilft nicht. */
  | "rejected"
  /** Netz, Server, Zeitüberschreitung – später erneut versuchen. */
  | "retry";

export class BackendError extends Error {
  constructor(
    readonly kind: ErrorKind,
    readonly code: string,
    message?: string,
  ) {
    super(message ?? code);
    this.name = "BackendFehler";
  }
}

export type ProgressRow = {
  readonly lesson_key: string;
  readonly stage_key: string;
  readonly accuracy: number;
  readonly stars: number;
};

/**
 * Ein Lehrgang, wie ihn die Anmeldung nennt.
 *
 * Titel und Untertitel kommen vom Server mit, damit die Auswahl beschriftet
 * werden kann, BEVOR ein Bundle geladen ist. Sonst müsste die App für zwei
 * Karten zwei Bundles holen – und eines davon wirft das Kind mit seiner Wahl
 * sofort wieder weg.
 */
/** Eine Messung des Kindes in diesem Lehrgang. */
export type Measurement = {
  readonly phase: "vorher" | "nachher";
  readonly chars: number;
  readonly seconds: number;
  readonly accuracy: number;
  readonly taken_at: string;
};

export type Course = {
  readonly slug: string;
  readonly title: string;
  readonly subtitle: string | null;
  readonly version: number;
  /** Kürzel des Vorgängers, oder null. Er muss zuerst zu Ende geübt sein. */
  readonly requires: string | null;
  /** Wie viele Stufen dieser Lehrgang hat. */
  readonly stages: number;
  /** Wie viele davon dieses Kind schon geübt hat. */
  readonly done: number;
  /** Schlüssel des Tests, oder null: dann arbeitet dieser Lehrgang ohne. */
  readonly test: string | null;
  /** Was dieses Kind darin schon gemessen hat – höchstens zwei Einträge. */
  readonly tests: readonly Measurement[];
  /**
   * Wie diese Klasse ihre Lektionen freigibt: `frei` oder `reihenfolge`.
   *
   * Optional, und das mit Absicht: die Lehrgänge kommen auch aus dem
   * `sessionStorage`, und ein Eintrag, der vor dieser Fassung dort landete,
   * kennt das Feld nicht. `gateFrom` macht aus allem Unbekannten „frei“.
   */
  readonly gate?: string | null;
  /** Sternschwelle der vorigen Lektion, oder null. Nur bei `reihenfolge`. */
  readonly gateStars?: number | null;
};

export type SignInResult = {
  /** Die Lehrgänge der Klasse, in der Reihenfolge, in der sie sie führt. */
  readonly courses: readonly Course[];
  readonly progress: readonly ProgressRow[];
};

/** Liest die Konfiguration; ohne sie läuft nur der Gastmodus. */
export function configFrom(
  env: Record<string, string | undefined>,
): BackendConfig | null {
  const url = env["VITE_SUPABASE_URL"];
  const anonKey = env["VITE_SUPABASE_ANON_KEY"];
  if (!url || !anonKey) return null;
  return { url: url.replace(/\/$/, ""), anonKey };
}

async function callBackend(
  config: BackendConfig,
  path: string,
  body: unknown,
  fetchFn: typeof fetch = fetch,
): Promise<unknown> {
  let response: Response;
  try {
    response = await fetchFn(`${config.url}${path}`, {
      method: "POST",
      headers: {
        apikey: config.anonKey,
        "content-type": "application/json",
      },
      body: JSON.stringify(body),
    });
  } catch (error) {
    // Offline, DNS, abgebrochen: immer wiederholbar.
    throw new BackendError("retry", "network_error", String(error));
  }

  if (response.ok) {
    if (response.status === 204) return null;
    return response.json();
  }

  // 429 gehört zu „später“: das Rate-Limit greift, wenn sich eine ganze Klasse
  // gleichzeitig anmeldet. Das Kind hat nichts falsch gemacht.
  if (response.status === 429 || response.status >= 500) {
    throw new BackendError("retry", `http_${response.status}`);
  }

  const parsed = (await response.json().catch(() => ({}))) as { message?: string };
  throw new BackendError("rejected", parsed.message ?? `http_${response.status}`);
}

/** Code-Login. Wirft bei falschem Code – ein leeres Ergebnis wäre nicht unterscheidbar. */
export async function signIn(
  config: BackendConfig,
  code: string,
  fetchFn: typeof fetch = fetch,
): Promise<SignInResult> {
  const response = (await callBackend(
    config,
    // Über die Edge Function, nicht direkt: hier sitzt die Bremsschwelle gegen
    // Durchprobieren. Der Vertrag ist derselbe, die Function reicht unverändert
    // an student_get_progress weiter.
    "/functions/v1/code-login",
    { p_code: code },
    fetchFn,
  )) as {
    valid?: boolean;
    curricula?: Course[];
    progress?: ProgressRow[];
  } | null;

  // Eine leere Liste wird wie ein falscher Code behandelt. Für das Kind ist es
  // dasselbe: es kann nicht üben. Der Server lässt es gar nicht erst so weit
  // kommen; hier steht es, weil ein leerer Bildschirm sonst der Ausweg wäre.
  if (!response?.valid || !response.curricula?.length) {
    throw new BackendError("rejected", "unexpected_response");
  }
  return { courses: response.curricula, progress: response.progress ?? [] };
}

export async function saveProgress(
  config: BackendConfig,
  input: {
    readonly code: string;
    readonly lessonKey: string;
    readonly stageKey: string;
    readonly accuracy: number;
  },
  fetchFn: typeof fetch = fetch,
): Promise<void> {
  await callBackend(
    config,
    "/rest/v1/rpc/student_save_progress",
    {
      p_code: input.code,
      p_lesson_key: input.lessonKey,
      p_stage_key: input.stageKey,
      p_accuracy: input.accuracy,
    },
    fetchFn,
  );
  // stars wird bewusst nicht mitgeschickt – das rechnet der Server.
}

/**
 * Eine Messung des Eingangs- oder Abschlusstests.
 *
 * Wie beim Fortschritt geht nur roh hinaus, was gemessen wurde: Zeichen,
 * Sekunden, Genauigkeit. Wörter pro Minute rechnet, wer sie anzeigt.
 *
 * Die dritte und letzte Funktion, die diese App ruft. Sie hat einen eigenen
 * Namen statt weiterer Argumente an `student_save_progress`: eine Funktion mit
 * zwei Bedeutungen, deren halbe Argumente immer null sind, wäre an beiden Enden
 * schwerer zu lesen.
 */
export async function saveTest(
  config: BackendConfig,
  input: {
    readonly code: string;
    readonly testKey: string;
    readonly phase: "vorher" | "nachher";
    readonly chars: number;
    readonly seconds: number;
    readonly accuracy: number;
  },
  fetchFn: typeof fetch = fetch,
): Promise<void> {
  await callBackend(
    config,
    "/rest/v1/rpc/student_save_test",
    {
      p_code: input.code,
      p_test_key: input.testKey,
      p_phase: input.phase,
      p_chars: input.chars,
      p_seconds: input.seconds,
      p_accuracy: input.accuracy,
    },
    fetchFn,
  );
}
